import { logger } from '@equip/utils/logger';
import { API_CONFIG } from '@/config/api';
import { useAuthStore } from '@/stores/useAuthStore';

const log = logger.child({ component: 'pdfHelpers' });

let windowCounter = 0;

/**
 * Opens a Blob in a new browser tab.
 * Uses an anchor element click which is more reliable than window.open
 * for opening multiple documents, especially after async operations.
 */
const openBlobInNewTab = (blob: Blob): boolean => {
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    return false;
  }

  try {
    const objectUrl = URL.createObjectURL(blob);
    const cleanupDelayMs = 60000;

    const link = document.createElement('a');
    link.href = objectUrl;
    link.style.display = 'none';
    // Unique target name ensures each document opens in its own tab
    link.target = `_doc_${++windowCounter}`;
    link.rel = 'noopener';
    document.body.appendChild(link);

    try {
      link.click();
      window.setTimeout(() => {
        try {
          URL.revokeObjectURL(objectUrl);
        } catch {
          /* ignore */
        }
      }, cleanupDelayMs);
      return true;
    } catch (clickError) {
      log.error('anchor click failed', { error: String(clickError) });
      URL.revokeObjectURL(objectUrl);
      return false;
    } finally {
      if (document.body.contains(link)) {
        document.body.removeChild(link);
      }
    }
  } catch (error) {
    log.error('Failed to open document in new tab', { error: String(error) });
    return false;
  }
};

/**
 * Pre-opens a browser window synchronously with a loading spinner.
 * MUST be called synchronously within a user-gesture (click) handler,
 * before any `await`, otherwise the browser will block it as a popup.
 *
 * Returns the opened window, or null if the browser blocked it.
 */
export const preOpenWindow = (): Window | null => {
  if (typeof window === 'undefined') return null;
  try {
    const w = window.open('about:blank', `_doc_${++windowCounter}`);
    if (w) {
      try {
        // Detect dark mode from persisted preference or system setting
        let isDark = false;
        try {
          const stored = JSON.parse(localStorage.getItem('equip_theme_prefs') || '{}');
          const mode = stored?.state?.themeMode ?? 'system';
          isDark =
            mode === 'dark' ||
            (mode === 'system' && matchMedia('(prefers-color-scheme: dark)').matches);
        } catch {
          /* use light fallback */
        }
        const bg = isDark ? '#121212' : '#f5f5f5';
        const fg = isDark ? 'rgba(255,255,255,0.7)' : '#555';
        const ring = isDark ? '#333' : '#e0e0e0';
        const accent = isDark ? '#6e69c4' : '#1976d2';
        w.document.write(
          '<html><head><title>Generating Document\u2026</title></head>' +
            `<body style="display:flex;align-items:center;justify-content:center;height:100vh;margin:0;font-family:system-ui,sans-serif;background:${bg}">` +
            '<div style="text-align:center">' +
            `<div style="width:40px;height:40px;border:4px solid ${ring};border-top-color:${accent};border-radius:50%;animation:s 1s linear infinite;margin:0 auto 16px"></div>` +
            `<p style="color:${fg};font-size:16px;margin:0">Generating document\u2026</p>` +
            '</div>' +
            '<style>@keyframes s{to{transform:rotate(360deg)}}</style>' +
            '</body></html>',
        );
        w.document.close();
      } catch {
        /* cross-origin or security restriction — window still usable */
      }
    }
    return w;
  } catch {
    return null;
  }
};

/**
 * Navigates a blob into a window — either a pre-opened one or a new tab.
 */
export const openBlobInWindow = (blob: Blob, target: Window | null | undefined): boolean => {
  const objectUrl = URL.createObjectURL(blob);
  const scheduleCleanup = () => {
    window.setTimeout(() => {
      try {
        URL.revokeObjectURL(objectUrl);
      } catch {
        /* ignore */
      }
    }, 60000);
  };

  // If we have a pre-opened window that's still alive, navigate it
  if (target && !target.closed) {
    try {
      target.location.href = objectUrl;
      scheduleCleanup();
      return true;
    } catch (err) {
      log.error('Failed to navigate pre-opened window', { error: String(err) });
      try {
        URL.revokeObjectURL(objectUrl);
      } catch {
        /* ignore */
      }
      try {
        target.close();
      } catch {
        /* ignore */
      }
    }
  }

  // Fallback: anchor click (may be blocked after async operations)
  return openBlobInNewTab(blob);
};

/**
 * Fetches a document URL with authentication and opens it in a tab.
 *
 * If a pre-opened window is provided (from `preOpenWindow`), the document
 * is loaded into that window. Otherwise falls back to an anchor click which
 * may be blocked by popup blockers after async operations.
 *
 * @param url - The document URL (relative or absolute)
 * @param preOpenedWindow - Optional window from `preOpenWindow()` called during user gesture
 * @returns true if the document was opened successfully, false otherwise
 */
/**
 * Opens a base64-encoded PDF in a pre-opened window or new tab.
 * Used to display PDFs returned inline from the API (skipping a download round-trip).
 */
export const openBase64PdfInWindow = (
  base64: string,
  target: Window | null | undefined,
): boolean => {
  if (typeof window === 'undefined') return false;
  try {
    const binaryStr = atob(base64);
    const bytes = new Uint8Array(binaryStr.length);
    for (let i = 0; i < binaryStr.length; i++) {
      bytes[i] = binaryStr.charCodeAt(i);
    }
    const blob = new Blob([bytes], { type: 'application/pdf' });
    return openBlobInWindow(blob, target);
  } catch (error) {
    log.error('Failed to open base64 PDF', { error: String(error) });
    if (target && !target.closed) target.close();
    return false;
  }
};

export const openDocumentWithAuth = async (
  url: string,
  preOpenedWindow?: Window | null,
): Promise<boolean> => {
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    return false;
  }

  try {
    const { accessToken } = useAuthStore.getState();

    // Build full URL if relative
    const fullUrl = url.startsWith('http') ? url : `${API_CONFIG.BASE_URL}${url}`;

    const response = await fetch(fullUrl, {
      method: 'GET',
      headers: {
        ...(accessToken && { Authorization: `Bearer ${accessToken}` }),
      },
    });

    if (!response.ok) {
      log.error('Failed to fetch document', {
        status: response.status,
        statusText: response.statusText,
        url: fullUrl,
      });
      if (preOpenedWindow && !preOpenedWindow.closed) preOpenedWindow.close();
      return false;
    }

    const blob = await response.blob();
    return openBlobInWindow(blob, preOpenedWindow);
  } catch (error) {
    log.error('Failed to open document with auth', { error: String(error), url });
    if (preOpenedWindow && !preOpenedWindow.closed) preOpenedWindow.close();
    return false;
  }
};
